import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLibrariesProps, LibraryView, VersionChangeRequest } from "./external-libraries.types";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * View-state + interaction logic for the External Libraries popover, kept separate from rendering.
 * The hook owns *transient UI state* (which tab, search text, what's expanded, optimistic loading
 * flags). The *data* (libraries/packages/versions) stays owned by the parent and flows in through
 * props; when it settles, the matching loading flag is cleared.
 */
export interface ExternalLibrariesController {
  visible: boolean;
  view: LibraryView;
  search: string;
  expanded: string | undefined;
  addingId: string | undefined;
  loadingLibraries: boolean;
  loadingPackages: boolean;
  loadingMore: boolean;
  loadingVersions: boolean;
  setVisible: (visible: boolean) => void;
  setView: (view: LibraryView) => void;
  setSearch: (query: string) => void;
  toggleExpanded: (libraryName: string) => void;
  add: (packageId: string, version: string) => void;
  remove: (libraryId: string, libraryName: string) => void;
  loadMore: () => void;
  selectVersion: (request: VersionChangeRequest) => void;
}

export const useExternalLibraries = (props: ExternalLibrariesProps): ExternalLibrariesController => {
  const { libraries, packages, versions, onOpen, onSearch, onExpand, onAdd, onDelete, onChangeVersion, onLoadMore } =
    props;
  const isModuleMode: boolean = props.sourceMode === "module";

  const [visible, setVisibleState] = useState<boolean>(false);
  const [view, setViewState] = useState<LibraryView>("installed");
  const [search, setSearchState] = useState<string>("");
  const [expanded, setExpanded] = useState<string | undefined>(undefined);
  const [addingId, setAddingId] = useState<string | undefined>(undefined);
  const [loadingLibraries, setLoadingLibraries] = useState<boolean>(false);
  const [loadingPackages, setLoadingPackages] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [loadingVersions, setLoadingVersions] = useState<boolean>(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parent-driven data has settled → release the matching optimistic loading flag.
  // (`addingId` is owned by `add()` itself, which clears it once the mutation settles.)
  useEffect(() => setLoadingLibraries(false), [libraries]);
  useEffect(() => {
    setLoadingPackages(false);
    setLoadingMore(false);
  }, [packages]);
  useEffect(() => setLoadingVersions(false), [versions]);

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    []
  );

  const setVisible = useCallback(
    (next: boolean): void => {
      setVisibleState(next);
      setViewState("installed");
      setSearchState("");
      setExpanded(undefined);
      if (next) {
        setLoadingLibraries(true);
        onOpen();
      }
    },
    [onOpen]
  );

  const setView = useCallback(
    (next: LibraryView): void => {
      setViewState(next);
      setSearchState("");
      setExpanded(undefined);
      if (next === "new") {
        // Module mode has nothing to browse — the "new" tab is just an add-by-path form.
        if (isModuleMode) return;
        setLoadingPackages(true);
        onSearch("", "new");
      } else {
        setLoadingLibraries(true);
        onOpen();
      }
    },
    [isModuleMode, onSearch, onOpen]
  );

  const setSearch = useCallback(
    (query: string): void => {
      setSearchState(query);
      // Installed-tab search filters in place (no fetch); browse-tab search hits the registry, debounced.
      if (view !== "new") return;
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        setLoadingPackages(true);
        onSearch(query, "new");
      }, SEARCH_DEBOUNCE_MS);
    },
    [view, onSearch]
  );

  const toggleExpanded = useCallback(
    (libraryName: string): void => {
      const next: string | undefined = expanded === libraryName ? undefined : libraryName;
      setExpanded(next);
      if (next) {
        setLoadingVersions(true);
        onExpand(next);
      }
    },
    [expanded, onExpand]
  );

  const add = useCallback(
    async (packageId: string, version: string): Promise<void> => {
      setAddingId(packageId);
      try {
        await onAdd(packageId, version);
      } finally {
        // Clears on success and on failure; the success path also refreshes the data props.
        setAddingId(undefined);
      }
    },
    [onAdd]
  );

  const remove = useCallback(
    async (libraryId: string, libraryName: string): Promise<void> => {
      setLoadingLibraries(true);
      try {
        await onDelete(libraryId, libraryName);
      } finally {
        setLoadingLibraries(false);
      }
    },
    [onDelete]
  );

  const loadMore = useCallback((): void => {
    // Distinct from loadingPackages so appending a page doesn't blur the already-rendered results.
    setLoadingMore(true);
    onLoadMore();
  }, [onLoadMore]);

  // Version change is confirmed inline in the detail (popover stays open). Drive the busy state and
  // clear it when the mutation settles — success or failure.
  const selectVersion = useCallback(
    async (request: VersionChangeRequest): Promise<void> => {
      setLoadingLibraries(true);
      try {
        await onChangeVersion(request.libraryId, request.libraryName, request.from, request.to);
      } finally {
        setLoadingLibraries(false);
      }
    },
    [onChangeVersion]
  );

  return {
    visible,
    view,
    search,
    expanded,
    addingId,
    loadingLibraries,
    loadingPackages,
    loadingMore,
    loadingVersions,
    setVisible,
    setView,
    setSearch,
    toggleExpanded,
    add,
    remove,
    loadMore,
    selectVersion
  };
};
